/**
 * i18next initialization for the widget.
 *
 * Locale JSON files in `./assets/locales/*.json` are bundled at build time and
 * auto-registered here, so adding a new language (e.g. a `de.json`) requires no
 * code change — just drop the file in that folder.
 *
 * The active language is resolved synchronously from the host (cookie /
 * `data-language` attribute) before the first render. It can also change at
 * runtime: see the language subscription in `stores/store-subscriptions.ts`,
 * which calls `i18n.changeLanguage(...)` when the config store updates.
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import { getLanguageCookie } from "@/utils/cookies";
import { resolveResourceLanguage } from "@/utils/language";

export const FALLBACK_LNG = "en";

// Eagerly bundle every locale JSON. Vite resolves the glob at build time.
const localeModules = import.meta.glob<{ default: Record<string, unknown> }>(
  "./assets/locales/*.json",
  { eager: true }
);

export const resources = Object.fromEntries(
  Object.entries(localeModules)
    .map(([path, mod]) => {
      // The region is part of the code: matching only `[a-z]{2}` registered `pt-br.json` under
      // "br", so Brazilian Portuguese loaded as a language nothing ever asks for (#3785).
      const lng = path.match(/([a-z]{2}(?:-[a-z]{2,4})?)\.json$/)?.[1];
      return lng ? [lng, { translation: mod.default }] : null;
    })
    .filter(
      (entry): entry is [string, { translation: Record<string, unknown> }] =>
        entry !== null
    )
);

const lng = resolveResourceLanguage(
  getLanguageCookie(),
  Object.keys(resources)
);

void i18n.use(initReactI18next).init({
  resources,
  lng,
  fallbackLng: FALLBACK_LNG,
  supportedLngs: Object.keys(resources),
  nonExplicitSupportedLngs: true,
  interpolation: {
    escapeValue: false, // React already escapes against XSS
  },
  returnNull: false,
  react: {
    // Resources are bundled and synchronous, so there is nothing to suspend on.
    useSuspense: false,
  },
});

export default i18n;
